// Bridgecore theme behaviour. Plain JS, no dependencies; every feature degrades to a working page.
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  // Copy discount code, model or UPC to the clipboard.
  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const ta = Object.assign(document.createElement('textarea'), { value: text });
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.append(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    }
  }

  document.addEventListener('click', async (e) => {
    const el = e.target.closest('[data-bc-copy]');
    if (!el) return;
    e.preventDefault();
    if (!(await copyText(el.dataset.bcCopy))) return;
    el.classList.add('bc-copied');
    const status = el.parentElement && $('[data-bc-copy-status]', el.parentElement);
    if (status) status.textContent = 'Copied';
    clearTimeout(el._bcT);
    el._bcT = setTimeout(() => {
      el.classList.remove('bc-copied');
      if (status) status.textContent = '';
    }, 2000);
  });

  // Glass header once the page scrolls.
  const hdr = $('[data-bc-hdr]');
  if (hdr) {
    const onScroll = () => hdr.classList.toggle('scrolled', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    requestAnimationFrame(onScroll);
  }

  // Product gallery: thumbs scroll the main strip; dots and thumbs follow the visible image.
  const gal = $('[data-bc-gallery]');
  if (gal) {
    const thumbs = $$('[data-bc-thumb]');
    const dots = $$('.bc-pp-dots i');
    thumbs.forEach((b) =>
      b.addEventListener('click', () => gal.scrollTo({ left: gal.clientWidth * Number(b.dataset.bcThumb), behavior: 'smooth' }))
    );
    gal.addEventListener(
      'scroll',
      () => {
        const i = Math.round(gal.scrollLeft / Math.max(gal.clientWidth, 1));
        thumbs.forEach((t, n) => t.classList.toggle('on', n === i));
        dots.forEach((d, n) => d.classList.toggle('on', n === i));
      },
      { passive: true }
    );
  }

  // Mobile buy bar: shows once the main add-to-cart button scrolls away, clicks it.
  const bar = $('[data-bc-buybar]');
  const mainBtn = $('[data-bc-product] form[data-type="add-to-cart-form"] button[type="submit"]');
  if (bar && mainBtn && 'IntersectionObserver' in window) {
    new IntersectionObserver(([en]) => {
      const show = !en.isIntersecting && en.boundingClientRect.top < 0;
      bar.classList.toggle('show', show);
      bar.setAttribute('aria-hidden', String(!show));
      $('[data-bc-buybar-btn]', bar).tabIndex = show ? 0 : -1;
    }).observe(mainBtn);
    $('[data-bc-buybar-btn]', bar).addEventListener('click', () => mainBtn.click());
  } else if (bar) {
    bar.remove();
  }

  // Filters and sort submit on change.
  $$('form[data-bc-autosubmit]').forEach((f) => {
    let t;
    f.addEventListener('change', () => f.requestSubmit ? f.requestSubmit() : f.submit());
    f.addEventListener('input', (e) => {
      if (e.target.type !== 'number') return;
      clearTimeout(t);
      t = setTimeout(() => f.requestSubmit ? f.requestSubmit() : f.submit(), 900);
    });
  });

  // Mobile filter panel toggle.
  const filtBtn = $('[data-bc-filt-btn]');
  if (filtBtn) {
    filtBtn.addEventListener('click', () => {
      const open = filtBtn.getAttribute('aria-expanded') !== 'true';
      filtBtn.setAttribute('aria-expanded', String(open));
      $('#bc-filters').classList.toggle('is-open', open);
    });
  }

  // Mobile bar "Search" focuses the header search instead of leaving the page.
  const q = $('[data-bc-search] input[name="q"]');
  $$('[data-bc-focus-search]').forEach((a) =>
    a.addEventListener('click', (e) => {
      if (!q) return;
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      q.focus({ preventScroll: true });
    })
  );

  // Mobile bar cart count follows Horizon's cart events.
  const count = $('[data-bc-cart-count]');
  if (count) {
    const setCount = (n) => {
      count.textContent = n;
      count.hidden = n === 0;
    };
    document.addEventListener('shopify:cart:lines-update', (e) => {
      e.promise?.then(({ cart, detail }) => setCount(cart?.totalQuantity ?? detail?.itemCount ?? 0)).catch(() => {});
    });
    // Back/forward restores an old page (bfcache or HTTP cache), so re-read the real count.
    const syncCount = () =>
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
        .then((r) => r.json())
        .then((c) => setCount(c.item_count))
        .catch(() => {});
    window.addEventListener('pageshow', (e) => { if (e.persisted) syncCount(); });
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') syncCount();
  }

  // Instant search: product suggestions from Shopify's predictive search endpoint.
  const form = $('[data-bc-search]');
  if (form && q) {
    const box = $('.bc-sres', form);
    let timer, ctrl, sel = -1;
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const close = () => { box.classList.remove('open'); q.setAttribute('aria-expanded', 'false'); sel = -1; };
    const fetchSuggest = async (term) => {
      ctrl?.abort();
      ctrl = new AbortController();
      const root = (window.Shopify?.routes?.root || '/');
      const url = `${root}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6`;
      const res = await fetch(url, { signal: ctrl.signal });
      const data = await res.json();
      return data?.resources?.results?.products || [];
    };
    q.addEventListener('input', () => {
      clearTimeout(timer);
      const term = q.value.trim();
      if (term.length < 2) return close();
      timer = setTimeout(async () => {
        try {
          const items = await fetchSuggest(term);
          box.innerHTML = items.length
            ? items.map((p) => `<a href="${esc(p.url)}" role="option"><img src="${esc(p.image || p.featured_image?.url || '')}" alt="" width="48" height="48" loading="lazy"><span><b>${esc(p.title)}</b><small>${esc(p.vendor)}</small></span><em>${esc(p.price ? '$' + Number(p.price).toFixed(2) : '')}</em></a>`).join('')
            : `<p>No quick matches for “${esc(term)}”. Press Enter for the full search.</p>`;
          box.classList.add('open');
          q.setAttribute('aria-expanded', 'true');
          sel = -1;
        } catch (err) {
          if (err.name !== 'AbortError') close();
        }
      }, 180);
    });
    form.addEventListener('keydown', (e) => {
      const links = $$('a', box);
      if (e.key === 'Escape') return close();
      if (!links.length || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      sel = (sel + (e.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
      links.forEach((a, i) => a.classList.toggle('sel', i === sel));
      links[sel].focus();
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
  }
})();
